import type {
  MenuItemProps,
  MenuPopupProps,
  MenuPortalProps,
  MenuTriggerProps,
} from '@base-ui/react/menu';
import { Menu } from '@base-ui/react/menu';
import type { ComponentProps } from 'react';

import { Icon } from '#/components/ui/icons';
import { useRootContainer } from '#/context/RootContainerContext';
import { cn } from '#/utils/cn';

export const DROPDOWN_MENU_TRIGGER_CLASS_NAME =
  'border-divider-subtle bg-gray-10 aria-expanded:border-blue-60 data-[popup-open]:border-blue-60 focus-visible:border-blue-60 inline-flex w-full items-center gap-8 rounded border px-16 py-12 text-sm sm:w-auto';

function DropdownMenuTrigger({
  className,
  children,
  ...props
}: MenuTriggerProps) {
  return (
    <Menu.Trigger
      className={cn(DROPDOWN_MENU_TRIGGER_CLASS_NAME, className)}
      {...props}
    >
      {children}

      <Icon
        id="chevron-right"
        size={12}
        className="text-fg-muted pointer-events-none ml-auto rotate-90"
      />
    </Menu.Trigger>
  );
}

function DropdownMenuPopup({
  children,
  matchTrigger = true,
  className,
  ...props
}: {
  matchTrigger?: boolean;
} & MenuPopupProps) {
  return (
    <Menu.Popup
      className={cn(
        'bg-gray-10 divide-divider border-divider-subtle overflow-hidden rounded border shadow-(--shadow-card)',
        'transition-[opacity,translate] duration-150 ease-[cubic-bezier(0.2,0,0,1)] will-change-transform',
        'data-ending-style:opacity-0 data-starting-style:opacity-0 data-[side=bottom]:data-ending-style:-translate-y-4 data-[side=bottom]:data-starting-style:-translate-y-4 data-[side=top]:data-ending-style:translate-y-4 data-[side=top]:data-starting-style:translate-y-4 motion-reduce:transform-none motion-reduce:transition-none',
        matchTrigger && 'w-(--anchor-width)',
        className,
      )}
      {...props}
    >
      {children}
    </Menu.Popup>
  );
}

function DropdownMenuItem({ className, children, ...props }: MenuItemProps) {
  return (
    <Menu.Item
      className={cn(
        className,
        'text-fg-header hover:bg-gray-20 focus:bg-gray-20 cursor-pointer p-12 text-sm',
      )}
      {...props}
    >
      {children}
    </Menu.Item>
  );
}

function DropdownMenuCheckboxItem({
  className,
  children,
  ...props
}: ComponentProps<typeof Menu.CheckboxItem>) {
  return (
    <Menu.CheckboxItem
      className={cn(
        className,
        'text-fg-header hover:bg-gray-20 focus:bg-gray-20 data-checked:bg-gray-20 flex cursor-pointer items-center gap-8 p-12 text-sm data-checked:font-semibold',
      )}
      {...props}
    >
      {children}
    </Menu.CheckboxItem>
  );
}

function DropdownMenuCheckboxItemIndicator({
  className,
  children,
  ...props
}: ComponentProps<typeof Menu.CheckboxItemIndicator>) {
  return (
    <Menu.CheckboxItemIndicator
      keepMounted
      className={cn(
        'border-form-border bg-gray-10 data-checked:border-button-primary-bg data-checked:bg-button-primary-bg data-checked:text-gray-10 flex size-16 shrink-0 items-center justify-center rounded-[3px] border text-transparent',
        className,
      )}
      {...props}
    >
      {children}
    </Menu.CheckboxItemIndicator>
  );
}

function DropdownMenuRadioItem({
  className,
  children,
  ...props
}: ComponentProps<typeof Menu.RadioItem>) {
  return (
    <Menu.RadioItem
      className={cn(
        className,
        'text-fg-header hover:bg-gray-20 focus:bg-gray-20 data-checked:bg-gray-20 flex cursor-pointer items-center justify-between gap-12 p-12 text-sm data-checked:font-semibold',
      )}
      {...props}
    >
      {children}
    </Menu.RadioItem>
  );
}

function DropdownMenuRadioItemIndicator({
  className,
  children,
  ...props
}: ComponentProps<typeof Menu.RadioItemIndicator>) {
  return (
    <Menu.RadioItemIndicator
      keepMounted
      className={cn(
        'text-fg-header flex w-16 shrink-0 items-center justify-center opacity-0 data-checked:opacity-100',
        className,
      )}
      {...props}
    >
      {children}
    </Menu.RadioItemIndicator>
  );
}

function DropdownMenuPortal({ children, ...props }: MenuPortalProps) {
  const rootContainer = useRootContainer();

  return (
    <Menu.Portal container={rootContainer ?? document.body} {...props}>
      {children}
    </Menu.Portal>
  );
}

export const DropdownMenu = {
  ...Menu,

  // portal component (needs root container)
  Portal: DropdownMenuPortal,

  // styled components
  Trigger: DropdownMenuTrigger,
  Popup: DropdownMenuPopup,
  Item: DropdownMenuItem,
  CheckboxItem: DropdownMenuCheckboxItem,
  CheckboxItemIndicator: DropdownMenuCheckboxItemIndicator,
  RadioItem: DropdownMenuRadioItem,
  RadioItemIndicator: DropdownMenuRadioItemIndicator,

  // base components (escape hatch if needed)
  BaseTrigger: Menu.Trigger,
  BasePopup: Menu.Popup,
  BaseItem: Menu.Item,
};
